/// <amd-module name="TFS/Services/TfsTeamService" />
import { WebApiTeam, CategorizedWebApiTeams } from "Tfs/Platform/Contracts/Core";
import { ICoreRestClient } from "Tfs/Platform/RestClient/Core";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";

/**
 * Service for working with teams
 */
export interface ITfsTeamService extends IVssService {
    /**
     * Get all teams all the teams user has read access to.
     *
     * @param projectId Optional id of a project to restrict the result to
     */
    getAllTeams(projectId?: string): Promise<WebApiTeam[]>;

    /**
     * Get teams current user is a member.
     *
     * @param projectId Optional id of a project to restrict the result to
     */
    getMyTeams(projectId?: string): Promise<WebApiTeam[]>;

    /**
     * Gets basic information about the team associated with the current page
     * (based on the navigation parameters)
     *
     * @param explicitTeamOnly If true, return undefined if the team was not explicitly
     * specified (through navigation parameter) in the page request. If false, this may
     * return the users' MRU team.
     */
    getPageTeam(explicitTeamOnly?: boolean): WebApiTeam | undefined;

    /**
     * Gets basic information about the teams this user can read and is member of.
     * Teams are split into member and readable with duplicates removed from readable list.
     */
    getTeamsCategorized(projectId: string): Promise<CategorizedWebApiTeams>;
}

interface ITeamDataProviderData {
    team?: WebApiTeam;
    explicit?: boolean;
}

const maxTeamsCount: number = 10000;

class TfsTeamService extends VssService implements ITfsTeamService {
    private allTeams: Promise<WebApiTeam[]>;
    private allTeamsByProject: { [projectId: string]: Promise<WebApiTeam[]> } = {};
    private myTeams: Promise<WebApiTeam[]>;
    private myTeamsByProject: { [projectId: string]: Promise<WebApiTeam[]> } = {};
    private myTeamsWithTypesByProject: { [projectId: string]: Promise<CategorizedWebApiTeams> } = {};

    public getAllTeams(projectId?: string): Promise<WebApiTeam[]> {
        let result: Promise<WebApiTeam[]>;
        if (projectId) {
            result = this.allTeamsByProject[projectId];
            if (!result) {
                const coreRestClient = this.pageContext.getRestClient<ICoreRestClient>("ICoreRestClient");
                result = coreRestClient.getTeams(projectId, undefined, maxTeamsCount /* top */);
                this.allTeamsByProject[projectId] = result;
            }
        } else {
            if (!this.allTeams) {
                const coreRestClient = this.pageContext.getRestClient<ICoreRestClient>("ICoreRestClient");
                this.allTeams = coreRestClient.getAllTeams(undefined, maxTeamsCount /* top */);
            }
            result = this.allTeams;
        }
        return result;
    }

    public getMyTeams(projectId?: string): Promise<WebApiTeam[]> {
        let result: Promise<WebApiTeam[]>;
        if (projectId) {
            result = this.myTeamsByProject[projectId];
            if (!result) {
                const coreRestClient = this.pageContext.getRestClient<ICoreRestClient>("ICoreRestClient");
                result = coreRestClient.getTeams(projectId, true, maxTeamsCount /* top */);
                this.myTeamsByProject[projectId] = result;
            }
        } else {
            if (!this.myTeams) {
                const coreRestClient = this.pageContext.getRestClient<ICoreRestClient>("ICoreRestClient");
                this.myTeams = coreRestClient.getAllTeams(true, maxTeamsCount /* top */);
            }
            return this.myTeams;
        }
        return result;
    }

    public getTeamsCategorized(projectId: string): Promise<CategorizedWebApiTeams> {
        let result: Promise<CategorizedWebApiTeams> = this.myTeamsWithTypesByProject[projectId];
        if (!result) {
            const coreRestClient = this.pageContext.getRestClient<ICoreRestClient>("ICoreRestClient");
            result = coreRestClient.getProjectTeamsByCategory(projectId, false, maxTeamsCount /* top */);
            this.myTeamsWithTypesByProject[projectId] = result;
        }
        return result;
    }

    public getPageTeam(explicitTeamOnly = false): WebApiTeam | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const teamData = contributionService.getData<ITeamDataProviderData>("ms.vss-tfs-web.team-data");

        if (teamData && (teamData.explicit || !explicitTeamOnly)) {
            return teamData.team;
        }

        return undefined;
    }
}

Services.add("ITfsTeamService", { serviceFactory: TfsTeamService });
